import { useCallback } from 'react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import CloseIcon from '../../images/close-icon.svg';
import { Column } from '../Column';
import { Pressable } from '../Pressable';
import { Tab, TabList, TabPanel, Tabs } from '../Tabs';
import { Text } from '../Text';
import {
  Container,
  FavoriteTabPanel,
  HighlyRatedTabPanel,
  Overlay,
  RecentProfessionalTabPanel,
} from './components';
import { ClickAwayListener } from '../ClickAwayListener';
import { useInviteProfessional } from './hooks';

interface IProps {
  isOpen: boolean;
  isRefetchJob: boolean;
  jobId?: string;
  jobIds?: string[];
  defaultProfessionals?: string[];
  profession?: string;
  onClose: () => void;
  onConfirm?: (ids: string[]) => void;
  onTrackEvent?: () => void;
}

export const InviteProfessionalPopup = ({
  isOpen,
  isRefetchJob,
  jobId,
  jobIds,
  defaultProfessionals,
  profession,
  onClose,
  onConfirm,
  onTrackEvent,
}: IProps) => {
  const {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange,
  } = useInviteProfessional({
    isRefetchJob,
    jobId,
    jobIds,
    defaultProfessionals,
    isOpen,
    onClosePopup: onClose,
  });

  const handleConfirmProfessionals = useCallback(() => {
    onTrackEvent?.();

    if (onConfirm) {
      onConfirm([...selectedProfessionals]);

      return;
    }

    onSentInvitations();
  }, [onConfirm, onTrackEvent, selectedProfessionals, onSentInvitations]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isSuccess ? (
        <Overlay>
          <ClickAwayListener onClickAway={() => onClosePopup()}>
            <Container>
              <Pressable onPress={onClosePopup}>
                <img
                  src={CloseIcon}
                  style={{
                    cursor: 'pointer',
                    height: 24,
                    position: 'absolute',
                    right: 20,
                    top: 20,
                    width: 24,
                  }}
                  alt="close_icon"
                />
              </Pressable>

              <Column marginBottom={24}>
                <Text bold fontSize={18} textAlign="center">
                  Invite Professionals
                </Text>
              </Column>

              <Tabs
                initialTab={activeTab}
                // @ts-expect-error -- Tabs still written in JavaScript
                onChangeTab={onTabChange}
                style={{ flex: 1 }}
                tabPanelStyle={{
                  display: 'flex',
                  flex: 1,
                }}
              >
                {/* @ts-expect-error -- TabList still written in JavaScript */}
                <TabList>
                  {/* @ts-expect-error -- Tab still written in JavaScript */}
                  <Tab
                    title="Favorites"
                    textStyle={{
                      fontSize: 20,
                    }}
                  />
                  {/* @ts-expect-error -- Tab still written in JavaScript */}
                  <Tab
                    title="Highly-Rated"
                    textStyle={{
                      fontSize: 20,
                    }}
                  />
                  {/* @ts-expect-error -- Tab still written in JavaScript */}
                  <Tab
                    title="Recents"
                    textStyle={{
                      fontSize: 20,
                    }}
                  />
                </TabList>
                <TabPanel>
                  <FavoriteTabPanel
                    isInviting={isInviting}
                    isLoading={isLoading}
                    selectedProfessionals={selectedProfessionals}
                    onSelectProfessionals={onSelectProfessionals}
                    onSentInvitations={handleConfirmProfessionals}
                    onClosePopup={onClosePopup}
                    isSelectionMode={!!onConfirm}
                    profession={profession}
                  />
                  <HighlyRatedTabPanel
                    isInviting={isInviting}
                    isLoading={isLoading}
                    selectedProfessionals={selectedProfessionals}
                    onSelectProfessionals={onSelectProfessionals}
                    onSentInvitations={handleConfirmProfessionals}
                    onClosePopup={onClosePopup}
                    isSelectionMode={!!onConfirm}
                    profession={profession}
                  />
                  <RecentProfessionalTabPanel
                    isInviting={isInviting}
                    isLoading={isLoading}
                    selectedProfessionals={selectedProfessionals}
                    onSelectProfessionals={onSelectProfessionals}
                    onSentInvitations={handleConfirmProfessionals}
                    onClosePopup={onClosePopup}
                    isSelectionMode={!!onConfirm}
                    profession={profession}
                  />
                </TabPanel>
              </Tabs>
            </Container>
          </ClickAwayListener>
        </Overlay>
      ) : (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <AnimatedCheckCircle />
            </div>
          )}
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonAction={onClosePopup}
          rightButtonText="Close"
        />
      )}
    </>
  );
};
